import {Helmet} from '@dr.pogodin/react-helmet';
import {Action, AlertProps, openConfirmDialog, showNotification} from 'platform/components';
import {Show} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {useEffect} from 'react';
// eslint-disable-next-line no-restricted-imports
import {useSearchParams} from 'react-router-dom';

import {always, defaultTo, isNil, isNotNil} from 'ramda';
import {concatAll, isNonEmptyArray, isNotNilOrEmpty} from 'ramda-adjunct';

import {EntityResourceIds} from '@dms/api/shared';
import {
  usePostServiceCaseActionCancelMutation,
  usePostServiceCaseActionReOpenMutation,
  usePostServiceOrderMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds, workshopRoutes} from '@dms/routes';
import {
  useTenant,
  DEFAULT_PRICE,
  DetailTemplate,
  DetailTemplateHeader,
  EntityHeaderControl,
  EntityHeaderFlag,
  ORDER_TABS,
  refreshDmsDatagrid,
  getCustomerName,
  handleApiError,
  queryParams,
  usePermissions,
  useStateFlag,
  workshopSearchParams,
  CheckoutIssuingProvider,
  useCheckoutIssuing,
} from '@dms/shared';
import {
  ServiceCaseDetailActionsProvider,
  ServiceOrderReturnMaterialModal,
  useServiceCaseDetailActions,
} from '@dms/shared-aftersales';

import {
  Nullish,
  buildArray,
  buildObject,
  composePath,
  useQueryState,
  useNavigate,
  openInNewTab,
} from 'shared/web';

import {SOURCE_DATA_GRID} from 'features/datagrid';

import {IntegrationsDropdown} from '../../components/IntegrationsDropdown/IntegrationsDropdown';
import {CHECKIN_SECTION, HANDOVER_SECTION} from '../../constants/detailSections';
import {useWorkshopUrl} from '../../hooks/useWorkshopUrl';
import {useXentryIntegration} from '../../hooks/useXentryIntegration';
import {getSparePartStateFlag} from '../../utils/getSparePartStateFlag';
import {getTimeTrackingStateFlag} from '../../utils/getTimeTrackingStateFlag';
import {
  SERVICE_CASE_CREATE_ORDER_ACTION,
  SERVICE_CASE_CANCEL_ACTION,
  SERVICE_CASE_REOPEN_ACTION,
} from './constants/actionKeys';
import {useOrdersWithMarginUnderMinimum} from './hooks/useOrdersWithMarginUnderMinimum';
import {useServiceCaseDetailData} from './hooks/useServiceCaseDetailData';
import {useServiceCaseDetailHandlers} from './hooks/useServiceCaseDetailHandlers';
import {useServiceCaseDetailSections} from './hooks/useServiceCaseDetailSections';
import {useServiceCaseVehicleHeaderParameters} from './hooks/useServiceCaseVehicleHeaderParameters';
import {getAdditionalInformationFlags} from './utils/getAdditionalInformationFlags';
import {getMarkersFlags} from './utils/getMarkersFlags';
import {getMenuItemsFromActions} from './utils/getMenuItemsFromActions';

export function ServiceCaseDetail() {
  return (
    <CheckoutIssuingProvider>
      <ServiceCaseDetailActionsProvider>
        <ServiceCaseDetailContent />
      </ServiceCaseDetailActionsProvider>
    </CheckoutIssuingProvider>
  );
}

function ServiceCaseDetailContent() {
  const {serviceCaseId, serviceOrderReturnId, serviceOrderReturnAuthorizationProfileId} =
    useWorkshopUrl();

  const {isCheckoutIssuing} = useCheckoutIssuing();
  const formatCurrency = useFormatCurrency();
  const tenant = useTenant();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [, setOrderId] = useQueryState(queryParams.SERVICE_CASE_ORDER_ID);
  const [, setOrderTab] = useQueryState(queryParams.SERVICE_CASE_ORDER_TAB);
  const [, , clearServiceOrderReturnId] = useQueryState(queryParams.SERVICE_ORDER_RETURN_ID);
  const [, , clearServiceOrderReturnAuthorizationProfileId] = useQueryState(
    queryParams.SERVICE_ORDER_RETURN_AUTHORIZATION_PROFILE_ID
  );

  const {executeAction, isAnyActionPending} = useServiceCaseDetailActions();

  const {data, isLoading, isError} = useServiceCaseDetailData({serviceCaseId});

  const [postServiceOrder] = usePostServiceOrderMutation();
  const [postServiceCaseActionCancel] = usePostServiceCaseActionCancelMutation();
  const [postServiceCaseActionReOpen] = usePostServiceCaseActionReOpenMutation();

  const [
    canReadChronos,
    canReadPneuhotel,
    canCreateServiceOrder,
    canReadServiceCase,
    canReadTasks,
  ] = usePermissions({
    permissionKeys: [
      'chronosUser',
      'pneuhotelRead',
      'serviceOrderCreate',
      'serviceCaseRead',
      'readTask',
    ],
  });

  const {
    serviceCase,
    checkIn,
    vehicle,
    comments,
    additionalNote,
    postponedJobs,
    orderVariants,
    vehicleCustomers,
    vehicleOperatorCustomer,
    integrationCustomer,
    integrationCustomerError,
    additionalInformation,
    vehicleTires,
    serviceCaseOrders,
    serviceCaseOrderCount,
    serviceCaseActions,
    recallMessage,
    documentCount,
    itemsTotalAmount,
    pendingServiceItems,
    pendingServiceOrderReturn,
    activeServiceCampaignsLength,
  } = data;

  const hasComments = comments.length > 0;
  const ordersWithMarginUnderMinimum = useOrdersWithMarginUnderMinimum({
    serviceCaseId,
    serviceCaseOrders,
  });

  const hasBookingUrl = isNotNilOrEmpty(serviceCase?.bookingUrl);
  const hasRepairUrl = isNotNilOrEmpty(serviceCase?.repairUrl);

  const {handlePendingLabourClick, handlePendingMaterialClick, handlePendingReturnClick} =
    useServiceCaseDetailHandlers({
      pendingServiceItems,
      pendingServiceOrderReturn,
      serviceCase,
    });

  const {xentryAlert} = useXentryIntegration({
    serviceCaseId,
    'data-testid': testIds.workshop.createServiceCase('xentry'),
  });

  const stateFlag = useStateFlag(serviceCase?.state);
  const ordersStateFlag = useStateFlag(serviceCase?.ordersState);
  const markersFlags = getMarkersFlags(serviceCase?.markers);
  const timeTrackingFlag = canReadChronos
    ? getTimeTrackingStateFlag(serviceCase?.timeTrackingState)
    : undefined;
  const sparePartFlag = getSparePartStateFlag(serviceCase?.sparePartWarehouseState);
  const additionalInformationFlags = getAdditionalInformationFlags(additionalInformation);

  const isCancelled = serviceCase?.state === 'CANCELLED';
  const isHandedOver = serviceCase?.state === 'HANDED_OVER';

  const vehicleHeaderParameters = useServiceCaseVehicleHeaderParameters(
    serviceCase,
    vehicle,
    checkIn,
    hasComments,
    additionalNote?.note
  );

  useEffect(() => {
    if (serviceCase?.state === 'DRAFT') {
      const searchParams = new URLSearchParams(window.location.search);
      navigate(
        composePath(workshopRoutes.draftServiceCase, {
          params: {id: serviceCaseId},
          queryParams: {
            tab: workshopSearchParams.create.serviceJobs,
            [SOURCE_DATA_GRID]: searchParams.get(SOURCE_DATA_GRID),
          },
        })
      );
    }
  }, [serviceCase?.state]);

  const handleNavigateCheckinHandover = (tab: string) => {
    searchParams.set(
      queryParams.COMPONENT_SECTIONS_SECTION,
      workshopSearchParams.serviceDetail.checkinHandover
    );
    searchParams.set(queryParams.SERVICE_CASE_CHECKIN_HANDOVER, tab);
    setSearchParams(searchParams);
  };

  const handleCreateOrder = (orderVariantId?: string | null) => {
    if (isNil(orderVariantId)) {
      return;
    }
    executeAction(SERVICE_CASE_CREATE_ORDER_ACTION, () =>
      postServiceOrder({serviceCaseId, body: {orderVariantId}})
        .unwrap()
        .then((order) => {
          const createdId = order?.id;
          if (isNil(createdId)) {
            return;
          }
          setOrderId(createdId);
          setOrderTab(ORDER_TABS.JOBS);
          showNotification.success(i18n.t('entity.order.labels.createSuccess'));
        })
        .catch(handleApiError)
    );
  };

  const handleActionClick = (actionKey: 'cancel' | 'reOpen' | Nullish) => {
    if (isNil(actionKey)) {
      return;
    }

    const confirmText = match(actionKey)
      .with(
        'cancel',
        always(i18n.t('entity.serviceCase.labels.cancelService', {name: serviceCase?.name}))
      )
      .with(
        'reOpen',
        always(i18n.t('entity.serviceCase.labels.reOpenService', {name: serviceCase?.name}))
      )
      .exhaustive();

    openConfirmDialog({
      text: confirmText,
      onConfirm: () => {
        match(actionKey)
          .with('cancel', () =>
            executeAction(SERVICE_CASE_CANCEL_ACTION, () =>
              postServiceCaseActionCancel({serviceCaseId: serviceCase?.id ?? ''})
                .unwrap()
                .catch(handleApiError)
            )
          )
          .with('reOpen', () =>
            executeAction(SERVICE_CASE_REOPEN_ACTION, () =>
              postServiceCaseActionReOpen({serviceCaseId: serviceCase?.id ?? ''})
                .unwrap()
                .catch(handleApiError)
            )
          )
          .exhaustive();
      },
    });
  };

  const sections = useServiceCaseDetailSections({
    serviceCaseId,
    canReadTasks,
    canReadServiceCase,
    canReadPneuhotel,
    serviceCase,
    serviceCaseOrderCount,
    documentCount,
    vehicleCustomers,
    vehicleTires,
    postponedJobs,
    activeServiceCampaignsLength,
  });

  const isBoshFleetClientWithoutPdf =
    isNotNil(integrationCustomerError) &&
    'status' in integrationCustomerError &&
    integrationCustomerError?.status === 404;

  const getHeader = buildObject<DetailTemplateHeader>()
    .title(`${vehicle?.title} - ${getCustomerName(vehicleOperatorCustomer)}`)
    .height(38)
    .resourceId(EntityResourceIds.serviceCase)
    .parkingRecordId(defaultTo(undefined, serviceCase?.vehicleId))
    .authorizationProfileRecordId(defaultTo(undefined, serviceCase?.authorizationProfileId))
    .recordId(defaultTo(undefined, serviceCase?.id))
    .controls(
      buildArray<EntityHeaderControl>()
        .whenNot(isHandedOver, 'PARKING')
        .when(serviceCase?.authorizationProfileId, 'AUTHORIZATION_PROFILE')
        .add('ASSIGNEE')
    )
    .icon('action/build')
    .flags(
      concatAll<EntityHeaderFlag[]>([
        buildArray<EntityHeaderFlag>()
          .when(stateFlag, stateFlag as EntityHeaderFlag)
          .when(ordersStateFlag, ordersStateFlag as EntityHeaderFlag)
          .when(isNotNil(timeTrackingFlag), timeTrackingFlag! as EntityHeaderFlag)
          .when(isNotNil(sparePartFlag), sparePartFlag! as EntityHeaderFlag),
        markersFlags,
        additionalInformationFlags,
      ])
    )
    .parameters(vehicleHeaderParameters)
    .primaryParameter(
      formatCurrency(
        defaultTo(DEFAULT_PRICE, itemsTotalAmount?.totalPrice?.withVat?.amount),
        defaultTo(tenant.tenantCurrency, itemsTotalAmount?.totalPrice?.withVat?.currency),
        2
      ) ?? '',
      Boolean(itemsTotalAmount?.totalPrice?.withVat)
    )
    .secondaryParameter(
      `${formatCurrency(
        defaultTo(DEFAULT_PRICE, itemsTotalAmount?.totalPrice?.withoutVat?.amount),
        defaultTo(tenant.tenantCurrency, itemsTotalAmount?.totalPrice?.withoutVat?.currency),
        2
      )} ${i18n.t('entity.serviceCase.labels.withoutVat')}`,
      Boolean(itemsTotalAmount?.totalPrice?.withoutVat)
    )
    .actions(
      buildArray<Action>()
        .when(
          isNonEmptyArray(
            getMenuItemsFromActions(() => {})(serviceCaseActions?.caseActions?.options)
          ),
          {
            type: 'dropdown-iconButton',
            icon: 'navigation/more_vert',
            priority: 'secondary',
            isDisabled: isAnyActionPending,
            menuItems: getMenuItemsFromActions(
              handleActionClick,
              isAnyActionPending
            )(serviceCaseActions?.caseActions?.options),
          }
        )
        .when(canCreateServiceOrder, {
          type: 'dropdown-button',
          variant: 'secondary',
          rightIcon: 'navigation/expand_more',
          title: i18n.t('entity.order.actions.createOrder'),
          isDisabled: isHandedOver || isCancelled || isAnyActionPending,
          menuItems:
            orderVariants?.map((variant) => ({
              label: variant?.name ?? '',
              onClick: () => handleCreateOrder(variant?.serviceOrderVariantId),
              isDisabled: isAnyActionPending,
            })) ?? [],
        })
        .whenNot(canCreateServiceOrder, {
          type: 'buttonWithTooltip',
          variant: 'secondary',
          rightIcon: 'navigation/expand_more',
          title: i18n.t('entity.order.actions.createOrder'),
          isDisabled: true,
          tooltip: {label: i18n.t('general.labels.noPermission')},
        })
        .add({
          type: 'custom',
          component: (
            <IntegrationsDropdown
              serviceCaseId={serviceCaseId}
              isExternalActionPending={isAnyActionPending}
            />
          ),
        })
        .when(hasBookingUrl, {
          type: 'button',
          variant: 'secondary',
          leftIcon: 'action/calendar_today',
          title: i18n.t('general.actions.schedule'),
          onClick: () => window.open(serviceCase?.bookingUrl as string, '_blank'),
          isDisabled:
            (serviceCase?.state !== 'CREATED' && serviceCase?.state !== 'CHECKED_IN') ||
            isAnyActionPending,
        })
        .when(hasRepairUrl, {
          type: 'button',
          variant: 'secondary',
          leftIcon: 'action/calendar_today',
          title: i18n.t('entity.serviceScheduler.actions.planner'),
          onClick: () => window.open(serviceCase?.repairUrl as string, '_blank'),
          isDisabled: isAnyActionPending,
        })
        .whenNot(checkIn, {
          type: 'button',
          title: i18n.t('entity.workshop.labels.checkIn'),
          variant: 'primary',
          onClick: () => handleNavigateCheckinHandover(CHECKIN_SECTION),
          isDisabled: isCancelled || isAnyActionPending,
        })
        .when(checkIn, {
          type: 'button',
          title: i18n.t('entity.workshop.labels.handover'),
          variant: isHandedOver ? 'secondary' : 'primary',
          onClick: () => handleNavigateCheckinHandover(HANDOVER_SECTION),
          isDisabled: isCancelled || isHandedOver || isAnyActionPending,
        })
    )
    .alerts(
      buildArray<AlertProps>()
        .when(recallMessage, {type: 'banner', message: recallMessage})
        .when(isNotNil(xentryAlert), xentryAlert as AlertProps)
        .when(
          isNotNilOrEmpty(pendingServiceItems?.workBasket) ||
            isNotNilOrEmpty(pendingServiceItems?.materialBasket),
          {
            type: 'banner',
            variant: 'warning',
            title: i18n.t('entity.serviceCase.labels.pendingItems'),
            hyperlinks: [
              {
                title: i18n.t('entity.serviceCase.actions.showLabour'),
                isDisabled: !isNotNilOrEmpty(pendingServiceItems?.workBasket),
                onClick: handlePendingLabourClick,
              },
              {
                title: i18n.t('entity.serviceCase.actions.showMaterial'),
                isDisabled: !isNotNilOrEmpty(pendingServiceItems?.materialBasket),
                onClick: handlePendingMaterialClick,
              },
            ],
          }
        )
        .when(integrationCustomer || isBoshFleetClientWithoutPdf, {
          type: 'banner',
          variant: 'info',
          title: i18n.t('entity.customer.labels.boschFleetCustomer'),
          message: i18n.t('entity.customer.labels.boschFleetCustomerInfo'),
          hyperlinks: [
            {
              title: i18n.t('entity.customer.labels.boschFleetTermsAndConditions'),
              isDisabled: isBoshFleetClientWithoutPdf,
              rightIcon: 'action/open_in_new',
              onClick: () => {
                if (integrationCustomer) {
                  openInNewTab(integrationCustomer);
                }
              },
            },
          ],
        })
        .when(pendingServiceOrderReturn, {
          title: i18n.t('entity.warehouse.notifications.youHavePendingItemsForReturn'),
          hyperlinks: [
            {
              title: i18n.t('entity.warehouse.actions.showPending'),
              onClick: handlePendingReturnClick,
            },
          ],
        })
        .when(isNonEmptyArray(ordersWithMarginUnderMinimum), {
          type: 'banner',
          variant: 'warning',
          message: i18n.t('entity.serviceCase.labels.marginUnderMinimumWarning', {
            orderNumbers: ordersWithMarginUnderMinimum.map((order) => order.number).join(', '),
          }),
        })
    )
    .build();

  return (
    <>
      <Helmet title={i18n.t('page.workshop.labels.serviceCase')} />
      <DetailTemplate
        isFullHeight
        isLocked={isCheckoutIssuing}
        isLoading={isLoading}
        isError={isError}
        header={getHeader}
        sections={sections}
        permissions={{
          tag: {
            isReadable: true,
            isCreatable: true,
            isUpdatable: true,
            isDeletable: true,
            isAssignable: true,
            isUnassignable: true,
          },
        }}
        defaultSection={workshopSearchParams.serviceDetail.orders}
        data-testid={testIds.workshop.serviceCaseDetail('page')}
      />
      <Show when={!!serviceOrderReturnId && !!serviceOrderReturnAuthorizationProfileId}>
        <ServiceOrderReturnMaterialModal
          isFromServiceOrder
          serviceOrderReturnId={serviceOrderReturnId}
          authorizationProfileId={serviceOrderReturnAuthorizationProfileId}
          onSubmit={() => {
            clearServiceOrderReturnId();
            clearServiceOrderReturnAuthorizationProfileId();
            refreshDmsDatagrid('service-case-order-items');
            refreshDmsDatagrid('service-case-order-job-items');
            refreshDmsDatagrid('service-case-order-items-returned');
          }}
          data-testid={testIds.workshop.serviceCaseDetail('returnMaterialModal')}
        />
      </Show>
    </>
  );
}
